import { useEffect, useState } from "react";
import { Check, Copy, MessageCircle, MessageSquare, Share2, X } from "lucide-react";
import { api, errText } from "../../lib/api";

/** Loads the unbranded French brief for an order once. */
const useBrief = (orderId) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    setData(null);
    setErr("");
    api.get(`/admin/orders/${orderId}/share`)
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setErr(errText(e)); });
    return () => { live = false; };
  }, [orderId]);
  return { data, err };
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    el.remove();
  }
};

/** WhatsApp / SMS / device share sheet / copy — plus the text itself unless `compact`. */
export const ShareBrief = ({ orderId, compact = false, testid = "order-share" }) => {
  const { data, err } = useBrief(orderId);
  const [copied, setCopied] = useState(false);

  if (err) return <p className="text-xs text-danger" data-testid={`${testid}-error`}>{err}</p>;
  if (!data) return <p className="text-xs text-muted" data-testid={`${testid}-loading`}>Preparing…</p>;

  const copy = async () => {
    await copyToClipboard(data.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    try {
      await navigator.share({ text: data.text });
    } catch { /* cancelled or unsupported */ }
  };

  return (
    <div className="space-y-3">
      {compact ? null : (
        <p className="text-xs text-muted">
          Delivery details only, in French, with nothing that names us.
          {data.florist_phone ? ` WhatsApp and SMS go straight to ${data.florist_name}.` : " No partner number on file — pick the contact yourself."}
          {data.has_pin ? "" : " No GPS pin on this order."}
        </p>
      )}
      <pre className="text-[11px] leading-relaxed whitespace-pre-wrap bg-plaster-deep/50 border border-brass-light p-3 max-h-56 overflow-auto"
           data-testid={`${testid}-preview`}>{data.text}</pre>
      <div className="flex flex-wrap gap-2">
        <a className="btn-primary text-[11px] px-3 no-underline" data-testid={`${testid}-whatsapp`}
           href={data.whatsapp_url} target="_blank" rel="noreferrer">
          <MessageCircle size={13} className="inline me-1 -mt-0.5" />WhatsApp
        </a>
        <a className="btn-ghost text-[11px] no-underline" data-testid={`${testid}-sms`} href={data.sms_url}>
          <MessageSquare size={13} className="inline me-1 -mt-0.5" />SMS
        </a>
        {typeof navigator !== "undefined" && navigator.share ? (
          <button type="button" className="btn-ghost text-[11px]" data-testid={`${testid}-native`} onClick={share}>
            <Share2 size={13} className="inline me-1 -mt-0.5" />Other apps
          </button>
        ) : null}
        <button type="button" className="btn-ghost text-[11px]" data-testid={`${testid}-copy`} onClick={copy}>
          {copied
            ? <><Check size={13} className="inline me-1 -mt-0.5" />Copied</>
            : <><Copy size={13} className="inline me-1 -mt-0.5" />Copy text</>}
        </button>
      </div>
    </div>
  );
};

/** Pops up on its own the moment an order is accepted, so the brief goes out there and then. */
export const ShareBriefModal = ({ orderId, orderNumber, accepted = false, onClose }) => (
  <div className="fixed inset-0 z-[60] scrim flex items-end sm:items-center justify-center p-0 sm:p-6"
       data-testid="share-brief-modal" onClick={onClose}>
    <div className="bg-plaster w-full sm:max-w-md border border-brass p-5 space-y-3 max-h-[92vh] overflow-y-auto"
         onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-3">
        <div>
          <h2 className="font-display text-xl text-ink">
            {accepted ? "Accepted — send the details" : "Send the details to the florist"}
          </h2>
          <p className="text-xs text-muted">
            {orderNumber ? `${orderNumber} · ` : ""}
            {accepted ? "The florist now needs the address and the card." : "Nothing in here names us."}
          </p>
        </div>
        <button type="button" className="ms-auto p-1 text-muted hover:text-ink" onClick={onClose}
                data-testid="share-brief-modal-close"><X size={18} /></button>
      </div>
      <ShareBrief orderId={orderId} compact testid="share-modal" />
      <button type="button" className="btn-ghost text-[11px] w-full" onClick={onClose} data-testid="share-brief-modal-done">
        Done
      </button>
    </div>
  </div>
);

export default ShareBrief;
